import { createSlice } from "@reduxjs/toolkit";
import {
  getPharmacyNotifications,
  markPharmacyNotificationRead,
  deletePharmacyNotification,
  getPharmacyUnreadNotificationCount,
  markAllPharmacyNotificationsRead,
} from "../../../reducer/pharmacy/notifications/pharmacyNotificationsReducer";
// Read state comes from the backend `isRead` flag, NOT from the FCM delivery `status`. The
// panel used to style a row unread whenever `status === 'sent'`, which never changes — so a
// clicked notification stayed highlighted even though the row was already `is_read = true` in
// the database. Same helper the doctor panel uses.
import {
  isNotificationUnread,
  getNotificationId,
} from "../../../utils/notificationReadState";

import i18n from "../../../i18n";
// Initial state
const initialState = {
  // Persistent notifications from API
  notifications: [],
  pagination: {
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  },
  unreadCount: 0,

  // Loading states
  loading: {
    fetch: false,
    markRead: false,
    delete: false,
    unreadCount: false,
    markAllRead: false,
  },

  // Error states
  error: {
    fetch: null,
    markRead: null,
    delete: null,
    unreadCount: null,
    markAllRead: null,
  },

  // Last fetch timestamp
  lastFetched: null,
};

const pharmacyNotificationsSlice = createSlice({
  name: "pharmacyNotifications",
  initialState,
  reducers: {
    // Add real-time notification from socket (prevents duplicates)
    addPharmacyRealtimeNotification: (state, action) => {
      const newNotification = action.payload;

      console.log(
        "[PHARMACY_SLICE] Received real-time notification:",
        newNotification,
      );
      console.log(
        "[PHARMACY_SLICE] Current unread count before:",
        state.unreadCount,
      );

      // Get notification ID from various possible fields
      const notificationId =
        newNotification.notification_id ||
        newNotification.id ||
        newNotification.recipient ||
        Date.now();

      // Check for duplicates based on notification_id
      const existingIndex = state.notifications.findIndex((notification) => {
        const existingId =
          notification.notification_id ||
          notification.id ||
          notification.recipient;
        return existingId === notificationId;
      });

      if (existingIndex === -1) {
        // Add new notification to the top
        const formattedNotification = {
          ...newNotification,
          id: notificationId,
          notification_id: notificationId,
          // Both casings, because the read check reads `isRead` first (that is what the API
          // sends) and would otherwise fall through to the delivery-status heuristic.
          isRead: false,
          is_read: false,
          created_at: newNotification.created_at || new Date().toISOString(),
        };

        state.notifications.unshift(formattedNotification);

        // Update unread count - increment by 1 for new notification
        state.unreadCount += 1;

        console.log(
          "[PHARMACY_SLICE] Added notification, new unread count:",
          state.unreadCount,
        );

        // Update pagination total
        state.pagination.total += 1;
      } else {
        console.log(
          "[PHARMACY_SLICE] Duplicate notification detected, skipping",
        );
      }
    },

    // Mark notification as read locally (optimistic update)
    markPharmacyAsReadLocally: (state, action) => {
      const notificationId = action.payload;
      const notification = state.notifications.find(
        (n) =>
          n.id === notificationId ||
          n.notification_id === notificationId ||
          n.recipient === notificationId,
      );

      // Write the flag the backend actually sends (`isRead`) and keep the snake_case mirror in
      // step, so the row reads as read both now and after the next refetch.
      if (notification && isNotificationUnread(notification)) {
        notification.isRead = true;
        notification.is_read = true;
        notification.readAt = new Date().toISOString();
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },

    // Remove notification locally (optimistic update)
    removePharmacyNotificationLocally: (state, action) => {
      const notificationId = action.payload;

      console.log(
        "[PHARMACY_SLICE] Attempting to remove notification with ID:",
        notificationId,
      );
      console.log(
        "[PHARMACY_SLICE] Current notifications:",
        state.notifications.map((n) => ({
          id: n.id,
          notification_id: n.notification_id,
          recipient: n.recipient,
        })),
      );

      const notificationIndex = state.notifications.findIndex((n) => {
        // Check all possible ID fields
        const matches =
          n.id === notificationId ||
          n.notification_id === notificationId ||
          n.recipient === notificationId;
        return matches;
      });

      if (notificationIndex !== -1) {
        const notification = state.notifications[notificationIndex];

        console.log(
          "[PHARMACY_SLICE] Removing notification locally:",
          notificationId,
        );

        // Remove from array
        state.notifications.splice(notificationIndex, 1);

        // Update unread count if it was unread. `!notification.is_read` was wrong on both
        // counts: the backend sends `isRead`, and it treated an already-read row as unread.
        if (isNotificationUnread(notification)) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }

        // Update pagination
        state.pagination.total = Math.max(0, state.pagination.total - 1);
      } else {
        console.warn(
          "[PHARMACY_SLICE] Notification not found for removal:",
          notificationId,
        );
      }
    },

    // Reset pharmacy notifications state
    resetPharmacyNotifications: (state) => {
      Object.assign(state, initialState);
    },

    // Update pharmacy unread count
    updatePharmacyUnreadCount: (state, action) => {
      state.unreadCount = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Get pharmacy notifications
    builder
      .addCase(getPharmacyNotifications.pending, (state) => {
        state.loading.fetch = true;
        state.error.fetch = null;
      })
      .addCase(getPharmacyNotifications.fulfilled, (state, action) => {
        state.loading.fetch = false;

        if (action.payload?.notifications) {
          const { notifications, pagination } = action.payload;

          // Ensure pagination exists with default values
          const safePagination = pagination || {
            page: 1,
            total: 0,
            totalPages: 0,
            limit: 20,
          };

          // For first page, replace all notifications
          // For subsequent pages, append
          if (safePagination.page === 1) {
            state.notifications = notifications.map((notification) => ({
              ...notification,
              id: notification.id || notification.notification_id,
            }));
          } else {
            // Filter out duplicates when appending
            const existingIds = new Set(
              state.notifications.map((n) => n.id || n.notification_id),
            );
            const newNotifications = notifications.filter(
              (notification) =>
                !existingIds.has(
                  notification.id || notification.notification_id,
                ),
            );

            state.notifications.push(
              ...newNotifications.map((notification) => ({
                ...notification,
                id: notification.id || notification.notification_id,
              })),
            );
          }

          state.pagination = safePagination;
          state.lastFetched = Date.now();
        }
      })
      .addCase(getPharmacyNotifications.rejected, (state, action) => {
        state.loading.fetch = false;
        state.error.fetch =
          action.payload?.message ||
          action.error?.message ||
          i18n.t("pharmacy.toast.failedToFetchPharmacyNotifications");
      });

    // Mark pharmacy notification as read
    builder
      .addCase(markPharmacyNotificationRead.pending, (state) => {
        state.loading.markRead = true;
        state.error.markRead = null;
      })
      .addCase(markPharmacyNotificationRead.fulfilled, (state, action) => {
        state.loading.markRead = false;

        // Confirm the optimistic update against the row the backend actually updated. The
        // service returns the persisted entity, so `notification.id` is the numeric primary key.
        // `isNotificationUnread` guards the decrement: the optimistic reducer has usually
        // already applied it, and decrementing twice made the badge drop by 2 per click.
        const updated =
          action.payload?.data?.notification ?? action.payload?.notification;
        if (updated) {
          const notification = state.notifications.find(
            (n) => getNotificationId(n) === updated.id,
          );
          if (notification) {
            if (isNotificationUnread(notification)) {
              state.unreadCount = Math.max(0, state.unreadCount - 1);
            }
            notification.isRead = true;
            notification.is_read = true;
            notification.readAt = updated.readAt || new Date().toISOString();
          }
        }
      })
      .addCase(markPharmacyNotificationRead.rejected, (state, action) => {
        state.loading.markRead = false;
        state.error.markRead =
          action.payload?.message ||
          action.error?.message ||
          i18n.t("pharmacy.toast.failedToMarkPharmacyNotificationAsRead");
      });

    // Delete pharmacy notification
    builder
      .addCase(deletePharmacyNotification.pending, (state) => {
        state.loading.delete = true;
        state.error.delete = null;
      })
      .addCase(deletePharmacyNotification.fulfilled, (state, action) => {
        state.loading.delete = false;

        // Update local state if needed
        if (action.payload?.notification_id) {
          const notificationIndex = state.notifications.findIndex(
            (n) =>
              (n.id || n.notification_id) === action.payload.notification_id,
          );

          if (notificationIndex !== -1) {
            const notification = state.notifications[notificationIndex];

            // Remove from array
            state.notifications.splice(notificationIndex, 1);

            // Update counts
            state.pagination.total = Math.max(0, state.pagination.total - 1);
            if (isNotificationUnread(notification)) {
              state.unreadCount = Math.max(0, state.unreadCount - 1);
            }
          }
        }
      })
      .addCase(deletePharmacyNotification.rejected, (state, action) => {
        state.loading.delete = false;
        state.error.delete =
          action.payload?.message ||
          action.error?.message ||
          i18n.t("pharmacy.toast.failedToDeletePharmacyNotification");
      });

    // Get pharmacy unread count
    builder
      .addCase(getPharmacyUnreadNotificationCount.pending, (state) => {
        state.loading.unreadCount = true;
        state.error.unreadCount = null;
      })
      .addCase(
        getPharmacyUnreadNotificationCount.fulfilled,
        (state, action) => {
          state.loading.unreadCount = false;

          console.log(
            "[PHARMACY_NOTIFICATIONS_SLICE] Unread count response:",
            action.payload,
          );

          // Handle both response formats for backward compatibility
          if (action.payload?.data?.unread_count !== undefined) {
            state.unreadCount = action.payload.data.unread_count;
            console.log(
              "[PHARMACY_NOTIFICATIONS_SLICE] Set unread count from data:",
              action.payload.data.unread_count,
            );
          } else if (action.payload?.unread_count !== undefined) {
            state.unreadCount = action.payload.unread_count;
            console.log(
              "[PHARMACY_NOTIFICATIONS_SLICE] Set unread count directly:",
              action.payload.unread_count,
            );
          } else {
            console.warn(
              "[PHARMACY_NOTIFICATIONS_SLICE] Unread count not found in response:",
              action.payload,
            );
          }
        },
      )
      .addCase(getPharmacyUnreadNotificationCount.rejected, (state, action) => {
        state.loading.unreadCount = false;
        state.error.unreadCount =
          action.payload?.message ||
          action.error?.message ||
          i18n.t("pharmacy.toast.failedToGetPharmacyUnreadCount");
      });

    // Mark all pharmacy notifications as read
    builder
      .addCase(markAllPharmacyNotificationsRead.pending, (state) => {
        state.loading.markAllRead = true;
        state.error.markAllRead = null;
      })
      .addCase(markAllPharmacyNotificationsRead.fulfilled, (state, action) => {
        state.loading.markAllRead = false;

        // Mark all notifications as read locally. Setting only `is_read` left every row still
        // rendering as unread, because the read check looks at `isRead` first.
        const readAt = new Date().toISOString();
        state.notifications.forEach((notification) => {
          notification.isRead = true;
          notification.is_read = true;
          notification.readAt = notification.readAt || readAt;
        });
        state.unreadCount = 0;
      })
      .addCase(markAllPharmacyNotificationsRead.rejected, (state, action) => {
        state.loading.markAllRead = false;
        state.error.markAllRead =
          action.payload?.message ||
          action.error?.message ||
          i18n.t("pharmacy.toast.failedToMarkAllPharmacyNotificationsAsRead");
      });
  },
});

// Re-export async thunks
export {
  getPharmacyNotifications,
  markPharmacyNotificationRead,
  deletePharmacyNotification,
  getPharmacyUnreadNotificationCount,
  markAllPharmacyNotificationsRead,
};

// Export actions
export const {
  addPharmacyRealtimeNotification,
  markPharmacyAsReadLocally,
  removePharmacyNotificationLocally,
  resetPharmacyNotifications,
  updatePharmacyUnreadCount,
} = pharmacyNotificationsSlice.actions;

// Export reducer
export default pharmacyNotificationsSlice.reducer;
